<template>
    <AppModal :title="$t('labels.editIntegrationProducts')" ref="modal" @on-hide="hide">
        <div class="modal-body">
            <div v-if="skippedTargetsCount > 0 && !task.state.isRunning && !shouldShowErrorTable" class="alert alert-warning border-0">
                {{ $t('alerts.compare_skipped_duplicate_targets', {total: skippedTargetsCount}) }}
            </div>
            <span v-if="products.length === 0 && skippedTargetsCount === 0">{{ $t('alerts.allProductsNotHaveTarget') }}</span>
            <template v-else-if="shouldShowErrorTable">
                <div class="alert alert-warning border-0 alert-dismissible mt-3">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    <span class="font-weight-semibold">{{ $t('labels.copy_products_warning_title') }}</span>
                    <p>{{ $t('labels.copy_products_warning_message') }}</p>
                    <p>{{ $t('labels.copy_products_warning_manual') }}</p>
                    <p>{{ $t('labels.copy_products_error_details') }}</p>
                </div>
                <div class="table-responsive table-scrollable">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_request') }}</th>
                                <th>{{ $t('labels.copy_products_response') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="badHttpHistoryItem in badHttpHistory">
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.request.body }}</span></td>
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.response.body }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else-if="shouldShowFieldSelection">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th width="60px">
                                    <AppCheckbox :model-value="areAllFieldsSelected" @update:model-value="selectAllFields" />
                                </th>
                                <th>{{ $t('labels.field') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="field in fieldsWhoCanBeMapped" :key="field.key">
                                <td>
                                    <AppCheckbox v-model="field.selected"/>
                                </td>
                                <td>{{ field.label }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else-if="task.state.isRunning">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </template>
        </div>
        <div class="modal-footer" v-if="shouldShowFieldSelection">
            <AppButton color="indigo" :disabled="areAllFieldsNotSelected" @click="updateIntegrationProduct">
                {{ $t('buttons.edit') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';

import { httpClient } from '../../../../services/HttpClient';
import HttpRequest from '../../../../services/Http/HttpRequest';
import HttpSender from '../../../../services/Http/HttpSender';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';

export default {
    name: "EditIntegrationProductsModal",
    components: {
        AppModal, AppButton, AppCheckbox, TaskProgressBar
    },
    props: {
        mainSourceIntegrationLanguage: {
            type: String
        }
    },
    emits: ['updateTable'],
    data() {
        return {
            task: createProgressTask(),
            products: [],
            skippedTargetsCount: 0,
            fieldsWhoCanBeMapped: [
                { key: 'source_id',         label: this.$t('labels.source_id'),                                     selected: false },
                { key: 'second_source_id',  label: this.$t('labels.second_source_id'),                              selected: false },
                { key: 'sku',               label: this.$t('labels.sku'),                                           selected: false },
                { key: 'names.uk',          label: `${this.$t('labels.name')} (${this.$t('languages.uk')})`,        selected: false },
                { key: 'names.ru',          label: `${this.$t('labels.name')} (${this.$t('languages.ru')})`,        selected: false },
                { key: 'descriptions.uk',   label: `${this.$t('labels.description')} (${this.$t('languages.uk')})`, selected: false },
                { key: 'descriptions.ru',   label: `${this.$t('labels.description')} (${this.$t('languages.ru')})`, selected: false },
                { key: 'seos.uk',           label: `${this.$t('labels.seo')} (${this.$t('languages.uk')})`,         selected: false },
                { key: 'seos.ru',           label: `${this.$t('labels.seo')} (${this.$t('languages.ru')})`,         selected: false },
                { key: 'images',            label: this.$t('labels.images'),                                        selected: false },
                { key: 'group_key',         label: this.$t('labels.group_key'),                                     selected: false },
                { key: 'manufacturer_name', label: this.$t('labels.manufacturer_name'),                             selected: false },
                { key: 'price',             label: this.$t('labels.price'),                                         selected: false },
                { key: 'sale_price',        label: this.$t('labels.sale_price'),                                    selected: false },
                { key: 'balance',           label: this.$t('labels.balance'),                                       selected: false }
            ],
            httpSender: new HttpSender(),
            badHttpHistory: []
        };
    },
    methods: {
        show(products) {
            // Several source publications of one product are paired with the same target publication, it is unclear which one to take.
            const countByTargetUuid = {};

            for (const product of products) {
                countByTargetUuid[product.target.uuid] = (countByTargetUuid[product.target.uuid] ?? 0) + 1;
            }

            this.products = products.filter(product => countByTargetUuid[product.target.uuid] === 1);
            this.skippedTargetsCount = Object.values(countByTargetUuid).filter(count => count > 1).length;
            this.$refs.modal.show();
        },
        hide() {
            this.task.cancel();
            this.$refs.modal.hide();
            this.products = [];
            this.skippedTargetsCount = 0;
            this.badHttpHistory = [];
            this.httpSender = new HttpSender();
            this.fieldsWhoCanBeMapped.forEach(field => {
                field.selected = false;
            });
        },
        async updateIntegrationProduct() {
            const fieldMappings = this.fieldsWhoCanBeMapped
                .filter(field => field.selected)
                .map(field => field.key);

            const requests = this.buildUpdateRequests(fieldMappings);

            const completed = await this.task.run(async ({setTotal, tick}) => {
                setTotal(requests.length);

                await this.httpSender.sendRequestsInBatches(requests, () => tick());
            });

            if (!completed) {
                return;
            }

            this.badHttpHistory = this.httpSender.getHistory().filter((historyItem) => {
                return historyItem.response.status !== 201 && historyItem.response.status !== 200;
            });

            this.$emit('updateTable');

            if (this.badHttpHistory.length === 0) {
                this.hide();
            }
        },
        buildUpdateRequests(fieldMappings) {
            const requests = [];

            for (let product of this.products) {
                const requestBody = {};

                for (let fieldKey of fieldMappings) {
                    const value = this.getNestedValue(product.source, fieldKey);
                    this.setNestedValue(requestBody, fieldKey, value);
                }

                requests.push(
                    new HttpRequest(
                        'PATCH',
                        `/integrations/products/${product.target.uuid}`,
                        requestBody
                    )
                );
            }

            return requests;
        },
        getNestedValue(obj, path) {
            return path.split('.').reduce((o, key) => (o && o[key] !== 'undefined') ? o[key] : null, obj);
        },
        setNestedValue(obj, path, value) {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const lastObj = keys.reduce((o, key) => {
                if (o[key] === undefined) o[key] = {};
                return o[key];
            }, obj);
            lastObj[lastKey] = value;
        },
        cleanSku(sku) {
            return sku.replace(/\s+/g, '');
        },
        selectAllFields(isSelected) {
            this.fieldsWhoCanBeMapped.forEach(field => field.selected = isSelected);
        },
    },
    computed: {
        areAllFieldsSelected() {
            return this.fieldsWhoCanBeMapped.every(field => field.selected);
        },
        areAllFieldsNotSelected() {
            return this.fieldsWhoCanBeMapped.every(field => !field.selected);
        },
        shouldShowErrorTable() {
            return !this.task.state.isRunning && this.badHttpHistory.length > 0;
        },
        shouldShowFieldSelection() {
            return !this.task.state.isRunning && this.badHttpHistory.length === 0 && this.products.length > 0;
        },
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>